import { Injectable, PLATFORM_ID, REQUEST, Renderer2, RendererFactory2, Signal, computed, inject } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';

import { ConfigService, CookieService } from '@fcom/core';

import {
  FINNAIR_THEME,
  FINNAIR_THEME_COOKIE_NAME,
  FinnairTheme,
  NEW_BRAND_STYLES_URL,
} from '../../interfaces/theme.interface';

const NEW_BRAND_STYLES_ELEMENT_ID = 'new-brand-styles';

@Injectable({ providedIn: 'root' })
export class FinnairThemeService {
  private cookieService = inject(CookieService);
  private platform = inject(PLATFORM_ID);
  private serverTheme = inject<FinnairTheme>(FINNAIR_THEME, { optional: true });
  private renderer2: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private document = inject<Document>(DOCUMENT);
  private configService = inject(ConfigService);
  private request = inject(REQUEST, { optional: true });
  private newBrandStylesUrl = inject(NEW_BRAND_STYLES_URL, { optional: true }) ?? 'new-brand.css';

  readonly siteTheme: Signal<FinnairTheme> = computed(() => {
    if (!this.configService.cfg.allowNewBrandTheming) {
      return FinnairTheme.LEGACY;
    }

    if (isPlatformBrowser(this.platform)) {
      return this.toValidTheme(this.cookieService.getItem(FINNAIR_THEME_COOKIE_NAME));
    }

    return this.serverTheme ?? this.toValidTheme(this.getRequestCookieTheme());
  });

  private toValidTheme(value: unknown): FinnairTheme {
    const isValidTheme = (Object.values(FinnairTheme) as string[]).includes(value as string);

    return isValidTheme ? (value as FinnairTheme) : FinnairTheme.LEGACY;
  }

  private getRequestCookieTheme(): string | undefined {
    const cookieHeader = this.request?.headers?.get('cookie');

    return cookieHeader
      ?.split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${FINNAIR_THEME_COOKIE_NAME}=`))
      ?.slice(FINNAIR_THEME_COOKIE_NAME.length + 1);
  }

  setTheme(theme: FinnairTheme): void {
    this.cookieService.setItem(FINNAIR_THEME_COOKIE_NAME, theme);
  }

  applyThemeClass(): void {
    const theme = this.siteTheme();
    const el = this.document.documentElement;

    if (el) {
      this.getHtmlElementClassNames(theme).forEach((className) => {
        this.renderer2.addClass(el, className);
      });

      if (theme !== FinnairTheme.LEGACY) {
        this.loadNewBrandStyles();
      }
    }
  }

  getHtmlElementClassNames(theme: FinnairTheme): string[] {
    if (theme === FinnairTheme.LIGHT_THEME) {
      return ['finnair-pro-fonts', 'light-theme'];
    }

    if (theme === FinnairTheme.DARK_THEME) {
      return ['finnair-pro-fonts', 'dark-theme'];
    }

    return ['finnair-fonts', 'legacy-theme'];
  }

  private loadNewBrandStyles(): void {
    if (this.document.getElementById(NEW_BRAND_STYLES_ELEMENT_ID)) {
      return;
    }
    const link: HTMLLinkElement = this.renderer2.createElement('link');
    this.renderer2.setAttribute(link, 'id', NEW_BRAND_STYLES_ELEMENT_ID);
    this.renderer2.setAttribute(link, 'rel', 'stylesheet');
    this.renderer2.setAttribute(link, 'href', this.newBrandStylesUrl);
    this.renderer2.appendChild(this.document.head, link);
  }
}
